import React, { useState, useEffect, useRef } from 'react';
import { API_URL, getImageUrl } from '../../utils/api';
import LearningStatusBadge from '../ui/LearningStatusBadge';
import ImageCarousel from '../common/ImageCarousel';
import { LoadingSpinner } from '../ui/Icons';
import Confetti from 'react-confetti';
import { GraduationCap } from 'lucide-react';

import QuizLayout from './shared/QuizLayout';
import QuizHeader from './shared/QuizHeader';

const MultipleChoiceQuiz = ({ deck, onFinish, plants, onReview, trackInteraction, userPlantData, onAbort, theme }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [options, setOptions] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isCorrect, setIsCorrect] = useState(null);
    const [results, setResults] = useState([]);
    const startTimeRef = useRef(Date.now());

    const currentCard = deck[currentIndex];
    const userCardData = userPlantData?.[currentCard?.id] || {};
    const currentStatus = userCardData.learning_status || 'NOT_LEARNED';

    useEffect(() => {
        startTimeRef.current = Date.now();
    }, [currentIndex]);

    useEffect(() => {
        if (!currentCard || !plants?.length) return;
        const distractors = plants.filter(p => p.category === currentCard.category && p.id !== currentCard.id)
            .sort(() => 0.5 - Math.random()).slice(0, 3);
        let choices = [currentCard, ...distractors];

        if (choices.length < 4) {
            const extra = plants.filter(p => p.id !== currentCard.id && !choices.find(c => c.id === p.id))
                .sort(() => 0.5 - Math.random()).slice(0, 4 - choices.length);
            choices = [...choices, ...extra];
        }

        setOptions(choices.sort(() => 0.5 - Math.random()));
        setSelectedAnswer(null);
        setIsCorrect(null);
    }, [currentIndex, currentCard, plants]);

    const handleAnswer = (option) => {
        if (selectedAnswer) return;
        const timeTaken = (Date.now() - startTimeRef.current) / 1000;
        const correct = option.id === currentCard.id;
        setSelectedAnswer(option);
        setIsCorrect(correct);

        const result = { plant: currentCard, correct, userAnswer: option };
        const updated = [...results, result];
        setResults(updated);

        if (onReview) onReview(currentCard, correct, null, timeTaken);

        if (trackInteraction) {
            trackInteraction(correct ? 'FLASHCARD_CORRECT' : 'FLASHCARD_INCORRECT', {
                plant_id: currentCard.id,
                time_taken: timeTaken,
                mode: 'multiple_choice'
            });
        }

        setTimeout(() => {
            if (currentIndex < deck.length - 1) setCurrentIndex(p => p + 1);
            else onFinish(updated);
        }, correct ? 1000 : 2500);
    };

    if (!currentCard) return <LoadingSpinner />;

    return (
        <QuizLayout onClose={onAbort} theme={theme} className="flex flex-col overflow-hidden">
            {isCorrect && <Confetti numberOfPieces={100} recycle={false} gravity={0.3} style={{ zIndex: 100 }} />}

            {/* Premium Header */}
            <QuizHeader
                current={currentIndex + 1}
                total={deck.length}
                onClose={onAbort}
                status={currentStatus}
                theme={theme}
            />

            {/* Workspace */}
            <div className="flex-1 flex flex-col min-h-0 space-y-3 md:space-y-4 px-4 md:px-12 pb-4 md:pb-8">
                {/* Image Container with Elegant Shadow */}
                <div className="flex-1 min-h-0 relative w-full aspect-[21/9] md:aspect-[21/9] bg-slate-900 rounded-[2rem] overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.6)] border border-white/10 group">
                    <ImageCarousel
                        images={currentCard.images}
                        alt={currentCard.german_name}
                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                        showThumbnails={false}
                    />

                    {/* Top Overlay - Exam Badge */}
                    {currentCard.is_intermediate_exam_subject && (
                        <div className="absolute top-3 left-3 z-40">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-400/95 backdrop-blur-xl border border-yellow-200/50 rounded-xl shadow-lg">
                                <GraduationCap className="w-4 h-4 text-yellow-950" />
                                <span className="text-[9px] md:text-[10px] font-black uppercase text-yellow-950 tracking-widest">Wichtig</span>
                            </div>
                        </div>
                    )}

                    {/* Question Overlay - Sophisticated Glassmorphism */}
                    <div className="absolute bottom-3 inset-x-3 p-3 md:p-6 bg-white/5 backdrop-blur-3xl border border-white/20 rounded-[1.5rem] shadow-2xl text-center z-30">
                        <span className="text-[9px] uppercase tracking-[0.4em] font-black text-plantlix-400 mb-1 block drop-shadow-md">Bestimmungs-Quiz</span>
                        <h3 className="text-lg md:text-3xl font-black text-white leading-tight tracking-tight italic">
                            Welche Pflanze ist das?
                        </h3>
                    </div>
                </div>

                {/* Options Area */}
                <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {options.map((opt, idx) => {
                        const isDark = theme === 'dark';
                        let optionClass = isDark
                            ? "bg-white/5 backdrop-blur-xl border border-white/10 hover:border-plantlix-500/50 hover:bg-white/15 text-white shadow-xl"
                            : "bg-white/60 backdrop-blur-xl border border-slate-200 hover:border-plantlix-500/50 hover:bg-white text-slate-900 shadow-md";

                        if (selectedAnswer) {
                            if (opt.id === currentCard.id) {
                                optionClass = "bg-plantlix-500 border-plantlix-400 text-white shadow-[0_0_30px_rgba(39,145,106,0.4)] ring-4 ring-plantlix-500/20 z-10 scale-[1.02]";
                            } else if (opt.id === selectedAnswer.id) {
                                optionClass = "bg-red-500 border-red-400 text-white shadow-[0_0_30px_rgba(239,68,68,0.4)] opacity-95 transition-none";
                            } else {
                                optionClass = isDark
                                    ? "bg-slate-900/40 border-transparent text-white/20 opacity-40 scale-[0.98]"
                                    : "bg-slate-100/40 border-transparent text-slate-300 opacity-40 scale-[0.98]";
                            }
                        }

                        return (
                            <button
                                key={idx}
                                onClick={() => handleAnswer(opt)}
                                disabled={!!selectedAnswer}
                                className={`
                                    relative px-5 py-3 md:px-6 md:py-4 rounded-3xl text-left transition-all duration-300 active:scale-95 flex items-center justify-between group overflow-hidden
                                    ${optionClass}
                                `}
                            >
                                <div className="flex flex-col min-w-0 pr-4">
                                    <span className="font-black text-lg md:text-2xl leading-tight truncate italic tracking-tight">
                                        {opt.botanical_name}
                                    </span>
                                    <span className="text-[9px] md:text-[10px] uppercase font-black opacity-50 tracking-[0.25em] truncate mt-1 min-h-[1.2rem]">
                                        {!selectedAnswer ? 'Botanischer Name gesucht' : opt.german_name}
                                    </span>
                                </div>
                                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shrink-0 border border-white/10 bg-white/5 transition-all duration-500
                                    ${selectedAnswer && opt.id === currentCard.id ? 'bg-white text-plantlix-600 border-transparent shadow-[0_0_15px_rgba(255,255,255,0.5)]' : ''}
                                    ${selectedAnswer && opt.id === selectedAnswer.id && opt.id !== currentCard.id ? 'bg-white text-red-600 border-transparent' : ''}
                                `}>
                                    {selectedAnswer && opt.id === currentCard.id && <span className="text-xl md:text-2xl font-black">✓</span>}
                                    {selectedAnswer && opt.id === selectedAnswer.id && opt.id !== currentCard.id && <span className="text-xl md:text-2xl font-black">✗</span>}
                                    {!selectedAnswer && <div className="w-2 h-2 rounded-full bg-white/20 group-hover:bg-plantlix-400 group-hover:scale-150 transition-all" />}
                                </div>
                                
                                {/* Background Highlight */}
                                <div className={`absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${selectedAnswer ? 'hidden' : ''}`} />
                            </button>
                        );
                    })}
                </div>
            </div>
        </QuizLayout>
    );
};

export default MultipleChoiceQuiz;
